import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box, Typography, Paper, CircularProgress, Chip, Alert,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
    ToggleButton, ToggleButtonGroup, useTheme, Tooltip, IconButton,
    Select, MenuItem, TableSortLabel, Fade, Button,
} from "@mui/material";
import BarChartIcon from "@mui/icons-material/BarChart";
import TableRowsIcon from "@mui/icons-material/TableRows";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
    ResponsiveContainer, ComposedChart, BarChart, LineChart, Bar, Line,
    XAxis, YAxis, CartesianGrid, Legend, Tooltip as RTooltip, LabelList, ReferenceLine,
} from "recharts";
import Header from "../../components/Header";
import v2Api from "../../utils/v2Api";

// Chart series colors — mid-tone on purpose so they read on BOTH a white (light
// mode) and a dark (dark mode) Paper surface. (The theme's accent tokens don't
// invert reliably between modes, so we don't pull series colors from them.)
const C = {
    new: "#2e9b76",   // New (growth green)
    ret: "#5562d6",   // Returning-from-last (loyal indigo)
    gap: "#c8860a",   // Returned-after-gap (win-back amber)
    line: "#d1453f",  // continuation rate (red line)
    line2: "#c026d3", // returning rate (fuchsia line — high contrast vs the bars/grid)
    line3: "#0891b2", // avg courses/student (cyan line, own right axis)
    line4: "#7c3aed", // % retained / returning incl. win-back (violet line)
    base: "#9aa0a6",  // comparison baseline (neutral grey)
};

// Per-season cell background (same season → same tint, so you can scan year-over-year) + label.
const SEASON_NAME = { 1: "Spring", 2: "Summer", 3: "Fall" };
const SEASON_BG = { 1: "#2e9b7630", 2: "#d1453f30", 3: "#5562d630" };

// Hover explanations shown on the chart legend keys.
const LEGEND_HINTS = {
    "New": "Students enrolling for the first time ever — no prior semester on record.",
    "Returning": "Students who were also enrolled the immediately previous semester (carry-over).",
    "Returned after gap": "Students enrolled before, who missed at least the previous semester and came back.",
    "Continuation %": "Of this semester's students, the % who returned the NEXT semester (forward retention — 'did we keep them'). Blank for the latest terms until the next one finishes.",
    "Returning %": "Of this semester's students, the % who were also enrolled the previous semester (returners) — the line version of the blue Returning bar.",
    "Avg courses/student": "Average courses each enrolled student took that term (total enrollments ÷ unique students). Higher = students taking more per term. Plotted on its own right axis.",
    "Retained %": "Share of this term's students who are returning customers — Returning (from last term) + Returned-after-gap (win-backs), i.e. everyone except brand-new students (= 100% − New %). How much of each term is your existing base.",
    "Retained": "Students carried over from the immediately previous semester (same as the blue Returning bar).",
    "Reactivated": "Students who came back after missing one or more semesters (a win-back) — counts as a gain.",
    "Lost": "Students who were enrolled last semester but did NOT return this one (churned out). Shown below zero.",
    "Net change": "Headcount this term minus last term = (New + Reactivated) − Lost. Above 0 = net growth, below 0 = net shrink.",
    "Headcount": "Total unique students enrolled that semester.",
    "Average": "Average survival across all cohorts at each point — a single 'typical cohort' retention curve. Later points average only the cohorts old enough to have reached them.",
    "Single-skip": "Took a course, skipped exactly one semester (often Summer), then returned.",
    "Previous semester": "New students in the immediately previous semester — the comparison baseline.",
    "Same season last year": "New students in the same season one year earlier — the comparison baseline.",
};

const pctLabel = (v) => (v === null || v === undefined ? "—" : `${v}%`);
const numLabel = (v) => (v === null || v === undefined ? "—" : v);
const netLabel = (n) => (n === null || n === undefined ? "—" : `${n > 0 ? "+" : ""}${n}`);

// Inline fraction (numerator over denominator) for the column-header formula tooltips.
const Frac = ({ n, d }) => (
    <Box component="span" sx={{ display: "inline-flex", flexDirection: "column", textAlign: "center", verticalAlign: "middle", mx: 0.5, lineHeight: 1.15 }}>
        <span>{n}</span>
        <span style={{ borderTop: "1px solid currentColor", display: "block" }}>{d}</span>
    </Box>
);
// Math expression (variable names only — not row numbers) shown when hovering a computed column header.
const COLUMN_FORMULA = {
    "New %": <span style={{ display: "inline-flex", alignItems: "center" }}><Frac n="New" d="Headcount" /> × 100</span>,
    "Retained %": <span style={{ display: "inline-flex", alignItems: "center" }}><Frac n="Returning + Returned-gap" d="Headcount" /> × 100</span>,
    "Carry-over %": <span style={{ display: "inline-flex", alignItems: "center" }}><Frac n="Returning" d="Headcount" /> × 100</span>,
    "Continuation %": <span style={{ display: "inline-flex", alignItems: "center" }}><Frac n="Returned next term" d="Headcount" /> × 100</span>,
    "Courses/student": <Frac n="Total enr." d="Headcount" />,
    "Lost": <span>prev. Headcount − Returning</span>,
    "Net": <span>Headcount − prev. Headcount</span>,
};

// Semester start/end → short "Aug 7 – Sep 2" range (UTC to avoid TZ drift). Dates come from semesters."start"/"end".
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtRange = (start, end) => {
    if (!start || !end) return "";
    const d = (x) => { const t = new Date(x); return `${MONTHS[t.getUTCMonth()]} ${t.getUTCDate()}`; };
    return `${d(start)} – ${d(end)}`;
};
// A course's own date range — can span years, so include the year (single day → one date).
const fmtCourseDates = (start, end) => {
    if (!start) return "—";
    const f = (x) => { const t = new Date(x); return `${MONTHS[t.getUTCMonth()]} ${t.getUTCDate()} '${String(t.getUTCFullYear()).slice(2)}`; };
    const a = f(start), b = end ? f(end) : null;
    const range = b && b !== a ? `${a} – ${b}` : a;
    if (!end) return range;
    const weeks = Math.round((new Date(end) - new Date(start)) / (7 * 86400000));
    const wk = weeks < 1 ? "<1 wk" : `${weeks} wk${weeks === 1 ? "" : "s"}`;
    return `${wk} (${range})`;
};

// Centered count inside each stacked segment — white with a soft dark outline so it stays legible
// on the colored bar AND when a small segment's number pokes out onto the background.
const SEG_LABEL = { fill: "#fff", fontSize: 11, fontWeight: 700, stroke: "rgba(0,0,0,0.5)", strokeWidth: 2, paintOrder: "stroke", strokeLinejoin: "round" };
const hideZero = (v) => (v ? v : "");

const LegendTips = ({ payload }) => (
    <Box display="flex" justifyContent="center" gap={2} flexWrap="wrap" sx={{ mt: 1 }}>
        {/* drop the dashed "tail" series — they share the solid line's legend entry */}
        {(payload || []).filter((e) => !String(e.dataKey ?? e.value ?? "").endsWith("__dash")).map((e) => {
            const hint = LEGEND_HINTS[e.value];
            return (
                <Tooltip key={e.value} title={hint || ""} arrow disableHoverListener={!hint}>
                    <Box display="flex" alignItems="center" gap={0.5} sx={{ cursor: hint ? "help" : "default" }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: "2px", backgroundColor: e.color }} />
                        <Typography variant="caption" sx={{ borderBottom: hint ? "1px dotted" : "none", borderColor: "text.disabled" }}>
                            {e.value}
                        </Typography>
                    </Box>
                </Tooltip>
            );
        })}
    </Box>
);

// Bilingual "how to read" copy for each card's info (ℹ️) tooltip.
const CARD_INFO = {
    composition: {
        en: "Each bar is a semester's total enrolled students, split into New (first-time), Returning (also enrolled last semester), and Returned-after-gap (back after missing 1+ semesters). The line is Returning % — the share of that semester who are returners from the previous term. Read it to see whether growth comes from new students (marketing) or kept students (retention). Use Counts/Mix % to switch between absolute numbers and each group's share.",
        kr: "각 막대는 해당 학기의 총 수강 학생 수로, 신규(첫 등록), 재등록(직전 학기에도 등록), 공백 후 복귀(한 학기 이상 쉬고 복귀)로 나뉩니다. 선은 재등록 %(직전 학기 대비 돌아온 학생 비율)입니다. 성장이 신규 유입(마케팅) 때문인지 기존 유지(리텐션) 때문인지 확인하세요. Counts/Mix % 토글로 실제 수와 그룹별 비중을 전환할 수 있습니다.",
    },
    newStudents: {
        en: "First-ever enrollments per semester (students whose very first semester is this one). The green bar is new students; the grey bar is your chosen baseline — the previous semester or the same season last year (use the toggle). The table below lists the exact change vs that baseline.",
        kr: "학기별 신규 등록 학생 수(첫 학기가 해당 학기인 학생). 초록 막대는 신규 학생, 회색 막대는 선택한 기준(직전 학기 또는 작년 동일 시즌, 토글로 전환)입니다. 아래 표에 기준 대비 정확한 증감이 표시됩니다.",
    },
    compare: {
        en: "Pick any two semesters (defaults to year-over-year — the latest term vs the same season a year earlier) and compare every metric side by side: headcount, new, returning, returned-after-gap, lost, net, continuation %, etc. The bars chart the student-count metrics; the table lists all of them with the change (Δ) and % change. Green = improved, red = worse (for Lost, lower is better).",
        kr: "아무 두 학기나 선택(기본값은 전년 동기 대비 — 최신 학기 vs 1년 전 같은 시즌)해 모든 지표를 나란히 비교합니다: 인원수, 신규, 재등록, 공백 후 복귀, 이탈, 순증감, 지속률 등. 막대는 학생 수 지표를, 표는 전체 지표를 변화량(Δ)·변화율과 함께 표시합니다. 초록 = 개선, 빨강 = 악화(이탈은 낮을수록 좋음).",
    },
    cohort: {
        en: "A cohort = students who first enrolled in the same semester. Each line/row follows one cohort over time: +0 is their first semester (always 100%), +1 is one semester later, +2 two later, and so on (3 semesters ≈ 1 year). The value is the % of that starting group still enrolled in any course at that point. Compare cohorts to see whether newer joiners stay longer. A * marks a semester that isn't finished yet.",
        kr: "코호트 = 같은 학기에 처음 등록한 학생 그룹. 각 선/행은 한 코호트를 시간순으로 추적합니다: +0은 첫 학기(항상 100%), +1은 한 학기 후, +2는 두 학기 후 식입니다(3학기 ≈ 1년). 값은 그 그룹 중 해당 시점에 어떤 과목이든 등록 중인 학생 비율입니다. 코호트를 비교해 최근 등록자가 더 오래 남는지 확인하세요. *는 아직 끝나지 않은 학기를 의미합니다.",
    },
    metrics: {
        en: "The full table behind every chart, grouped by year. Headcount = unique students; Total enr. = total course enrollments (one student in 3 courses counts 3×). Season dots are colored so you can scan the same season across years. Also New / New %, Returning & Carry-over %, Returned-gap, Single-skip, Continuation % (% of this semester's students who returned the next semester — blank until that semester ends), Courses/student. Badges flag sparse / in-progress / future.",
        kr: "모든 차트의 기반이 되는 표 (연도별로 묶음). Headcount = 고유 학생 수, Total enr. = 총 수강 건수(한 학생이 3과목이면 3으로 카운트). 계절 색 점으로 같은 계절을 연도별로 비교할 수 있어요. 그 외 신규/신규 %, 재등록 및 Carry-over %, 공백 후 복귀, 한 학기 건너뜀, 지속률(다음 학기에 돌아온 비율 — 다음 학기 끝나야 표시), 학생당 과목 수.",
    },
    flow: {
        en: "Net flow of students each term. Above zero = gained (New first-timers + Reactivated win-backs); below zero = Lost (were enrolled last term, didn't return). The line is net change = (gains − losses). Tells you at a glance whether you're growing, and whether it's from new students or from keeping existing ones.",
        kr: "학기별 학생 순증감. 0 위 = 유입(신규 + 공백 후 복귀); 0 아래 = 이탈(직전 학기 등록했으나 미복귀). 선 = 순변화(유입 − 이탈). 성장 여부와, 그 성장이 신규 유입 때문인지 기존 유지 때문인지 한눈에 보여줍니다.",
    },
    waterfall: {
        en: "Each bar is that term's headcount split by source — Retained (carried over) + New + Reactivated — which sum to the headcount line. The translucent red cap on top is who was Lost since last term (churn). Lets you see growth, its composition, and losses together.",
        kr: "각 막대는 해당 학기 인원을 출처별로 나눈 것 — 유지(이월) + 신규 + 재활성 — 이며 합이 인원 수 선과 같습니다. 위의 반투명 빨강 = 직전 학기 대비 이탈한 학생. 성장과 그 구성, 이탈을 함께 볼 수 있어요.",
    },
};

// Info (ℹ️) button for a card's bottom-right corner; hover shows EN + KR how-to-read.
const InfoTip = ({ en, kr }) => (
    <Tooltip
        arrow
        placement="top-end"
        componentsProps={{ tooltip: { sx: { maxWidth: 380 } } }}
        title={
            <Box sx={{ p: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>How to read</Typography>
                <Typography variant="caption" sx={{ display: "block", mb: 1 }}>{en}</Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>읽는 법</Typography>
                <Typography variant="caption" sx={{ display: "block" }}>{kr}</Typography>
            </Box>
        }
    >
        <IconButton size="small" aria-label="how to read this chart" sx={{ position: "absolute", bottom: 6, right: 8, color: "text.disabled" }}>
            <InfoOutlinedIcon fontSize="small" />
        </IconButton>
    </Tooltip>
);

// Panel with an optional chart↔table view toggle and a controls slot in the header.
const ChartPanel = ({ title, subtitle, controls, chart, table, info, footer }) => {
    const [view, setView] = useState("chart");
    return (
        <Paper sx={{ p: 2.5, pb: info ? 5 : 2.5, mb: 3, borderRadius: 2, position: "relative" }} elevation={2}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} flexWrap="wrap">
                <Box sx={{ flex: 1, minWidth: 220 }}>
                    <Typography variant="h4" fontWeight={700}>{title}</Typography>
                    {subtitle && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{subtitle}</Typography>}
                </Box>
                <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                    {controls}
                    {table && (
                        <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
                            <ToggleButton value="chart" aria-label="chart view"><BarChartIcon fontSize="small" /></ToggleButton>
                            <ToggleButton value="table" aria-label="table view"><TableRowsIcon fontSize="small" /></ToggleButton>
                        </ToggleButtonGroup>
                    )}
                </Box>
            </Box>
            <Box sx={{ mt: 2 }}>{view === "table" && table ? table : chart}</Box>
            {footer && <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>{footer}</Box>}
            {info && <InfoTip {...info} />}
        </Paper>
    );
};

// Sortable per-semester "which courses ran" table, shown under the composition chart.
// Driven by active.courseBreakdown (already in the selected attribution mode).
const COURSE_COLS = [
    { key: "code", label: "Code", align: "left", num: false },
    { key: "name", label: "Course", align: "left", num: false },
    { key: "subject", label: "Subject", align: "left", num: false },
    { key: "modules", label: "Modules", align: "right", num: true },
    { key: "courseStart", label: "Course dates", align: "left", num: false }, // sorts by start date (ISO sorts chronologically)
    { key: "students", label: "Students", align: "right", num: true },
];

const CourseBreakdown = ({ breakdown, sems, value, onChange }) => {
    const [sortBy, setSortBy] = useState("students");
    const [dir, setDir] = useState("desc");

    // Controlled: parent picks the semester (clicking a bar or this dropdown).
    const sel = sems.find((s) => s.label === value) || null;
    const key = sel ? `${Number(sel.year)}-${Number(sel.season)}` : null;

    const rows = useMemo(() => (key && breakdown?.[key]) || [], [key, breakdown]);

    const sorted = useMemo(() => {
        const col = COURSE_COLS.find((c) => c.key === sortBy);
        return [...rows].sort((a, b) => {
            if (col?.num) {
                const av = Number(a[sortBy]) || 0, bv = Number(b[sortBy]) || 0;
                return dir === "asc" ? av - bv : bv - av;
            }
            const av = String(a[sortBy] || "").toLowerCase(), bv = String(b[sortBy] || "").toLowerCase();
            return dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
        });
    }, [rows, sortBy, dir]);

    const onSort = (k) => {
        if (k === sortBy) { setDir((d) => (d === "asc" ? "desc" : "asc")); return; }
        setSortBy(k);
        setDir(COURSE_COLS.find((c) => c.key === k)?.num ? "desc" : "asc");
    };

    const totalStudents = rows.reduce((n, r) => n + r.students, 0);

    return (
        <Box>
            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" mb={1.5}>
                <Typography variant="body1" fontWeight={700}>Courses registered in</Typography>
                <Select size="small" value={sel?.label || ""} onChange={(e) => onChange(e.target.value)} sx={{ minWidth: 150 }}>
                    {sems.map((s) => <MenuItem key={s.label} value={s.label}>{s.label}</MenuItem>)}
                </Select>
                <Typography variant="caption" color="text.secondary">
                    {rows.length} course{rows.length === 1 ? "" : "s"} · {totalStudents} registrations · <b>click a bar above</b> to switch semester · click a header to sort
                </Typography>
            </Box>
            <TableContainer sx={{ maxHeight: 440 }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            {COURSE_COLS.map((c) => (
                                <TableCell key={c.key} align={c.align} sortDirection={sortBy === c.key ? dir : false}
                                    sx={{ fontWeight: 700, whiteSpace: "nowrap", bgcolor: "background.paper" }}>
                                    <TableSortLabel active={sortBy === c.key} direction={sortBy === c.key ? dir : "desc"}
                                        onClick={() => onSort(c.key)}>
                                        {c.label}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sorted.length === 0 && (
                            <TableRow><TableCell colSpan={COURSE_COLS.length}>
                                <Typography variant="body2" color="text.secondary">No courses found for this semester.</Typography>
                            </TableCell></TableRow>
                        )}
                        {sorted.map((r, i) => (
                            <TableRow key={`${r.code}-${i}`} hover>
                                <TableCell><Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }} color="text.secondary">{r.code}</Typography></TableCell>
                                <TableCell><Typography variant="body2" fontWeight={600}>{r.name}</Typography></TableCell>
                                <TableCell><Typography variant="body2" color="text.secondary">{r.subject || "—"}</Typography></TableCell>
                                <TableCell align="right">{r.modules}</TableCell>
                                <TableCell><Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>{fmtCourseDates(r.courseStart, r.courseEnd)}</Typography></TableCell>
                                <TableCell align="right">{r.students}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

const Panel = ({ title, subtitle, children, info }) => (
    <Paper sx={{ p: 2.5, pb: info ? 5 : 2.5, mb: 3, borderRadius: 2, position: "relative" }} elevation={2}>
        <Typography variant="h4" fontWeight={700}>{title}</Typography>
        {subtitle && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>{subtitle}</Typography>}
        {children}
        {info && <InfoTip {...info} />}
    </Paper>
);

// Metrics shown in the semester comparator. `bar: true` = also charted (same student-count unit).
const COMPARE_ROWS = [
    { key: "headcount", label: "Headcount", get: (s) => s.headcount, fmt: numLabel, bar: true },
    { key: "newCount", label: "New", get: (s) => s.newCount, fmt: numLabel, bar: true },
    { key: "returningFromLast", label: "Returning", get: (s) => s.returningFromLast, fmt: numLabel, bar: true },
    { key: "returnedAfterGap", label: "Returned-after-gap", get: (s) => s.returnedAfterGap, fmt: numLabel, bar: true },
    { key: "lost", label: "Lost", get: (s, f) => f?.lostAbs ?? null, fmt: numLabel, bar: true, inverse: true },
    { key: "net", label: "Net change", get: (s, f) => f?.net ?? null, fmt: netLabel },
    { key: "enrollmentRows", label: "Total enrollments", get: (s) => s.enrollmentRows, fmt: numLabel },
    { key: "continuationRate", label: "Continuation %", get: (s) => s.continuationRate, fmt: pctLabel },
    { key: "carryOverRate", label: "Returning %", get: (s) => s.carryOverRate, fmt: pctLabel },
    { key: "newRate", label: "New %", get: (s) => s.newRate, fmt: pctLabel },
    { key: "retainedPct", label: "Retained %", get: (s) => (s.headcount ? Math.round((s.returningFromLast + s.returnedAfterGap) / s.headcount * 1000) / 10 : null), fmt: pctLabel },
    { key: "enrollmentsPerStudent", label: "Avg courses/student", get: (s) => s.enrollmentsPerStudent, fmt: numLabel },
];

// Bar/column color per selected semester (cycles if more than 7 are compared).
const COMPARE_PALETTE = ["#9aa0a6", "#5562d6", "#2e9b76", "#c8860a", "#d1453f", "#0891b2", "#c026d3"];

// Compare N semesters (defaults to a year-over-year pair) → every metric, charted + tabled.
const SemesterCompare = ({ sems, flowByLabel }) => {
    const theme = useTheme();
    const axisColor = theme.palette.text.secondary;
    const gridColor = theme.palette.divider;
    const DEFAULT_BARS = COMPARE_ROWS.filter((m) => m.bar).map((m) => m.key);

    const defaults = useMemo(() => {
        const started = sems.filter((s) => !s.flags?.future);
        const b = started[started.length - 1] || sems[sems.length - 1];
        if (!b) return [];
        const yoy = sems.find((s) => Number(s.year) === Number(b.year) - 1 && Number(s.season) === Number(b.season));
        const idxB = sems.indexOf(b);
        const a = yoy || sems[idxB - 1] || b;
        return [...new Set([a?.label, b?.label].filter(Boolean))];
    }, [sems]);
    const [selLabels, setSelLabels] = useState(defaults);
    const [barKeys, setBarKeys] = useState(DEFAULT_BARS);

    const selected = useMemo(() => selLabels.map((l) => sems.find((s) => s.label === l)).filter(Boolean), [selLabels, sems]);
    const available = sems.filter((s) => !selLabels.includes(s.label));
    const baseline = selected[0];

    const barData = useMemo(() => COMPARE_ROWS.filter((m) => barKeys.includes(m.key)).map((m) => {
        const row = { metric: m.label };
        selected.forEach((s) => { row[s.label] = m.get(s, flowByLabel[s.label]); });
        return row;
    }), [selected, barKeys, flowByLabel]);

    const deltaColor = (m, d) => {
        if (d == null || d === 0) return "inherit";
        return (m.inverse ? d < 0 : d > 0) ? C.new : C.line;
    };

    return (
        <Box>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={2}>
                <Typography variant="body1" fontWeight={700} sx={{ mr: 0.5 }}>Compare</Typography>
                {selected.map((s, i) => (
                    <Chip key={s.label} label={s.label} size="small"
                        onDelete={() => setSelLabels((ls) => ls.filter((l) => l !== s.label))}
                        sx={{ bgcolor: COMPARE_PALETTE[i % COMPARE_PALETTE.length], color: "#fff", fontWeight: 600, "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.7)", "&:hover": { color: "#fff" } } }} />
                ))}
                {available.length > 0 && (
                    <Select size="small" value="" displayEmpty sx={{ minWidth: 110 }}
                        onChange={(e) => e.target.value && setSelLabels((ls) => [...ls, e.target.value])}
                        renderValue={() => <span style={{ color: theme.palette.text.disabled }}>+ Add</span>}>
                        {available.map((s) => <MenuItem key={s.label} value={s.label}>{s.label}</MenuItem>)}
                    </Select>
                )}
            </Box>

            {selected.length === 0 ? (
                <Box sx={{ height: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Typography variant="h6" color="text.secondary">Add a semester to compare</Typography>
                </Box>
            ) : (<>
            <Box display="flex" gap={0.75} flexWrap="wrap" alignItems="center" mb={1.5}>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Chart metrics:</Typography>
                {COMPARE_ROWS.map((m) => {
                    const on = barKeys.includes(m.key);
                    return (
                        <Chip key={m.key} label={m.label} size="small" clickable
                            color={on ? "primary" : "default"} variant={on ? "filled" : "outlined"}
                            onClick={() => setBarKeys((ks) => (on ? ks.filter((k) => k !== m.key) : [...ks, m.key]))} />
                    );
                })}
                <Button size="small" onClick={() => setBarKeys(DEFAULT_BARS)} sx={{ textTransform: "none", ml: 0.5 }}>Reset</Button>
                <Typography variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>(mixing %s and counts shares one axis)</Typography>
            </Box>

            {barKeys.length > 0 && (
                <Box sx={{ height: 340 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={barData} margin={{ top: 16, right: 20, bottom: 10, left: 0 }}>
                            <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
                            <XAxis dataKey="metric" tick={{ fill: axisColor, fontSize: 11 }} interval={0} angle={-12} textAnchor="end" height={50} />
                            <YAxis tick={{ fill: axisColor, fontSize: 12 }} />
                            <RTooltip contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, borderRadius: 6 }} />
                            <Legend />
                            {selected.map((s, i) => (
                                <Bar key={s.label} dataKey={s.label} fill={COMPARE_PALETTE[i % COMPARE_PALETTE.length]} radius={[3, 3, 0, 0]} animationDuration={400} />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            )}

            <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Metric</TableCell>
                            {selected.map((s) => <TableCell key={s.label} align="right" sx={{ fontWeight: 700 }}>{s.label}</TableCell>)}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {COMPARE_ROWS.map((m) => {
                            const baseV = baseline ? m.get(baseline, flowByLabel[baseline.label]) : null;
                            return (
                                <TableRow key={m.key} hover>
                                    <TableCell>{m.label}</TableCell>
                                    {selected.map((s, i) => {
                                        const v = m.get(s, flowByLabel[s.label]);
                                        const d = (i === 0 || v == null || baseV == null) ? null : v - baseV;
                                        return (
                                            <TableCell key={s.label} align="right" sx={{ color: i === 0 ? "inherit" : deltaColor(m, d), fontWeight: i === 0 ? 400 : 600 }}>
                                                {m.fmt(v)}
                                                {d != null && <Typography component="span" variant="caption" sx={{ ml: 0.4, opacity: 0.85 }}>({netLabel(d)})</Typography>}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            </>)}
        </Box>
    );
};

const RetentionDashboard = () => {
    const theme = useTheme();
    const dark = theme.palette.mode === "dark";

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [selSem, setSelSem] = useState(null);         // semester label for the course-breakdown (click a bar or the dropdown)
    const [seriesVis, setSeriesVis] = useState({ bars: true, continuation: true, returning: true, avg: true, retained: false }); // which series show (retained off by default to avoid clutter)
    const [showCounts, setShowCounts] = useState(false); // bar number labels — hidden by default (cleaner)
    const [flowView, setFlowView] = useState("net");     // gains/losses card: 'net' (diverging) | 'source' (by-source + churn)

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const res = await v2Api.get("/analytics/retention");
            setData(res.data?.data || null);
        } catch (err) {
            console.error("Failed to load retention analytics:", err);
            setError(err.response?.data?.message || err.message || "Failed to load");
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { loadData(); }, [loadData]);

    // Always the date-accurate ("attendance dates") view — the only counting method we show.
    const active = data?.window || data; // fall back to flat shape for safety
    // Drop empty terms (0 headcount) — e.g. Summer 2023 only existed as a stray course tag;
    // under attendance-dates nobody was actually enrolled, so it's not real data.
    const sems = (active?.semesters || []).filter((s) => s.headcount > 0);

    // Effective semester for the course-breakdown table (click a bar or use the dropdown).
    const breakdownSemLabel = useMemo(() => {
        if (selSem && sems.some((s) => s.label === selSem)) return selSem;
        const started = [...sems].reverse().find((s) => !s.flags?.future);
        return (started || sems[sems.length - 1])?.label || "";
    }, [selSem, sems]);

    // Current semester = the in-progress one; fall back to the latest already-started semester.
    const currentSemester = useMemo(
        () => sems.find((s) => s.flags.inProgress) || [...sems].reverse().find((s) => !s.flags.future) || null,
        [sems]
    );

    const compositionData = useMemo(() => {
        const rows = sems.map((s) => ({
            label: s.label,
            New: s.newCount,
            Returning: s.returningFromLast,
            "Returned after gap": s.returnedAfterGap,
            continuation: s.continuationRate,
            carryOver: s.carryOverRate,
            headcount: s.headcount,        // unique students (= sum of the 3 segments)
            total: s.enrollmentRows,       // total enrollments (non-dedup seat count)
            avgCourses: s.enrollmentsPerStudent, // avg courses per student that term (~1–2)
            // % of this term that are returning customers (Returning + win-back) = 100% − New %.
            retainedPct: s.headcount ? Math.round((s.returningFromLast + s.returnedAfterGap) / s.headcount * 1000) / 10 : null,
            provisional: !!(s.flags?.inProgress || s.flags?.future), // term not finished → values provisional
            continuationPending: !!s.continuationPending,            // continuation provisional (next term still running)
        }));
        // Split each line into a SOLID part (final values) + a DASHED tail (provisional values), the
        // boundary point shared so they connect. continuation's provisional-ness is its OWN pending flag
        // (next term in progress); carryOver/avg are provisional when the term itself isn't finished.
        const split = (key, isProv) => {
            const firstProv = rows.findIndex((r) => isProv(r) && r[key] != null);
            const boundary = firstProv === -1 ? rows.length - 1 : firstProv - 1;
            rows.forEach((r, i) => {
                r[`${key}__solid`] = i <= boundary ? r[key] : null;
                r[`${key}__dash`] = (firstProv !== -1 && i >= boundary) ? r[key] : null;
            });
        };
        split("continuation", (r) => r.continuationPending);
        split("carryOver", (r) => r.provisional);
        split("avgCourses", (r) => r.provisional);
        split("retainedPct", (r) => r.provisional);
        return rows;
    }, [sems]);

    // Gains/losses flow per term. Lost = last term's headcount minus those who returned (the churned).
    // All derivable from data we already have (headcount + returningFromLast).
    const flowData = useMemo(() => sems.map((s, i) => {
        const prev = sems[i - 1];
        const lost = prev ? Math.max(0, prev.headcount - s.returningFromLast) : null;
        const net = prev ? s.headcount - prev.headcount : null;
        return {
            label: s.label,
            Retained: s.returningFromLast,   // carried over from last term (= Returning)
            New: s.newCount,
            Reactivated: s.returnedAfterGap,
            // Retained + New + Reactivated === headcount (partition identity)
            Lost: lost === null ? null : -lost, // negative → renders below zero (diverging chart)
            lostAbs: lost,                       // positive → red cap (waterfall)
            net,
            headcount: s.headcount,
        };
    }), [sems]);
    const flowByLabel = useMemo(() => Object.fromEntries(flowData.map((f) => [f.label, f])), [flowData]);

    // Group semesters by year for the merged-year detail table.
    const semsByYear = useMemo(() => {
        const out = [];
        for (const s of sems) {
            const last = out[out.length - 1];
            if (last && last.year === s.year) last.rows.push(s);
            else out.push({ year: s.year, rows: [s] });
        }
        return out;
    }, [sems]);

    const cohorts = active?.cohorts || [];
    const maxOffset = useMemo(() => cohorts.reduce((m, c) => Math.max(m, c.survival.length), 0), [cohorts]);
    const cohortSeries = useMemo(() => cohorts.filter((c) => c.newStudents > 0), [cohorts]);
    // Cohort rows matched to displayed semesters (drops filtered-out empty terms), grouped by year for the table.
    const cohortsByYear = useMemo(() => {
        const rows = cohorts
            .map((c) => { const sem = sems.find((s) => s.label === c.label); return sem ? { c, year: sem.year, season: sem.season } : null; })
            .filter(Boolean);
        const out = [];
        for (const r of rows) {
            const last = out[out.length - 1];
            if (last && last.year === r.year) last.rows.push(r);
            else out.push({ year: r.year, rows: [r] });
        }
        return out;
    }, [cohorts, sems]);
    const cohortLineData = useMemo(() => Array.from({ length: maxOffset }).map((_, k) => {
        const row = { offset: `+${k}` };
        cohortSeries.forEach((c) => { row[c.label] = c.survival[k]?.rate ?? null; });
        // Average survival across cohorts at this offset (mean of the cohorts old enough to have reached it).
        const rates = cohortSeries.map((c) => c.survival[k]?.rate).filter((v) => v != null);
        row.Average = rates.length ? Math.round((rates.reduce((a, b) => a + b, 0) / rates.length) * 10) / 10 : null;
        return row;
    }), [cohortSeries, maxOffset]);
    const cohortColor = (i, n) => `hsl(${Math.round((i / Math.max(1, n - 1)) * 280)}, 65%, ${dark ? 62 : 42}%)`;

    const cohortCellColor = (rate) => {
        if (rate === null || rate === undefined) return "transparent";
        const h = (rate / 100) * 120; // 0=red → 120=green (heatmap)
        return `hsl(${h}, 78%, ${dark ? 40 : 66}%)`; // saturated enough to read at a glance
    };


    // Theme-aware chart chrome (readable in both light and dark).
    const axisColor = theme.palette.text.secondary;
    const gridColor = theme.palette.divider;

    // X-axis tick: semester name on top, its date range in italics below.
    const semRange = Object.fromEntries(sems.map((s) => [s.label, fmtRange(s.start, s.end)]));
    const renderSemTick = ({ x, y, payload }) => {
        const r = semRange[payload.value];
        return (
            <g transform={`translate(${x},${y}) rotate(-25)`}>
                <text x={0} y={0} dy={10} textAnchor="end" fill={axisColor} fontSize={12}>{payload.value}</text>
                {r && <text x={0} y={0} dy={23} textAnchor="end" fill={axisColor} fillOpacity={0.6} fontSize={10} fontStyle="italic">{r}</text>}
            </g>
        );
    };
    const tooltipStyle = {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        color: theme.palette.text.primary,
    };

    // Composition chart tooltip — shows the 3 segments + BOTH unique headcount and total enrollments.
    const compTooltip = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null;
        const d = payload[0]?.payload || {};
        const row = (name, val, color) => (
            <Box key={name} sx={{ display: "flex", justifyContent: "space-between", gap: 3 }}>
                <span style={{ color }}>{name}</span><b>{val}</b>
            </Box>
        );
        return (
            <Box sx={{ ...tooltipStyle, p: 1.2, fontSize: 12, minWidth: 180 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>{label}</Typography>
                {payload.filter((p) => ["New", "Returning", "Returned after gap"].includes(p.name))
                    .map((p) => row(p.name, p.value, p.color))}
                <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, mt: 0.5, pt: 0.5 }}>
                    {row("Unique headcount", d.headcount, theme.palette.text.primary)}
                    {row("Total enrollments", d.total, theme.palette.text.secondary)}
                    {d.continuation != null && row("Continuation %", `${d.continuation}%`, C.line)}
                    {d.carryOver != null && row("Returning %", `${d.carryOver}%`, C.line2)}
                    {d.retainedPct != null && row("Retained %", `${d.retainedPct}%`, C.line4)}
                    {d.avgCourses != null && row("Avg courses/student", d.avgCourses, C.line3)}
                </Box>
            </Box>
        );
    };

    if (loading) {
        return (
            <Box m="20px" display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }
    if (error) {
        return (
            <Box m="20px">
                <Header title="Retention Analytics" subtitle="재등록 / 신규 학생 통계" />
                <Alert severity="error">Could not load analytics: {error}</Alert>
            </Box>
        );
    }

    // --- Composition card (chart + table) ---
    // Render only the axes the visible series need (cleaner); they stack when several are on.
    const leftMax = Math.ceil((Math.max(0, ...sems.map((s) => s.headcount || 0)) * 1.05) / 10) * 10 || 10;
    const showPctAxis = seriesVis.continuation || seriesVis.returning || seriesVis.retained;
    const anyComposition = seriesVis.bars || showPctAxis || seriesVis.avg;
    const refAxis = seriesVis.bars ? "left" : showPctAxis ? "right" : seriesVis.avg ? "avg" : null;
    const compositionChart = (
        <Box sx={{ height: 620, position: "relative" }}>
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={compositionData} margin={{ top: 28, right: 20, bottom: 30, left: 0 }}
                    onClick={(e) => e?.activeLabel && setSelSem(e.activeLabel)} style={{ cursor: "pointer" }}>
                    <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
                    {refAxis && <ReferenceLine yAxisId={refAxis} x={breakdownSemLabel} stroke={C.line} strokeWidth={2} strokeDasharray="4 3" strokeOpacity={0.6} />}
                    <XAxis dataKey="label" tick={renderSemTick} interval={0} height={80} />
                    {/* Each y-axis renders only when its series is shown; multiple stack to the right. */}
                    {seriesVis.bars && (
                        <YAxis yAxisId="left" domain={[0, leftMax]} tick={{ fill: axisColor, fontSize: 12 }}
                            label={{ value: "students", angle: -90, position: "insideLeft", fill: axisColor }} />
                    )}
                    {showPctAxis && (
                        <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fill: axisColor, fontSize: 12 }} label={{ value: "%", angle: 90, position: "insideRight", fill: axisColor }} />
                    )}
                    {seriesVis.avg && (
                        <YAxis yAxisId="avg" orientation="right" width={50} domain={[0, (m) => Math.max(2, Math.ceil(m * 1.3))]}
                            allowDecimals={false} tick={{ fill: C.line3, fontSize: 11 }}
                            label={{ value: "courses / student", angle: 90, position: "insideRight", fill: C.line3, fontSize: 11 }} />
                    )}
                    <RTooltip content={compTooltip} />
                    <Legend content={<LegendTips />} />
                    {/* Stack bottom→top: Returning (existing base) → Returned-after-gap → New (growth on top). */}
                    {seriesVis.bars && (
                        <Bar yAxisId="left" dataKey="Returning" stackId="a" fill={C.ret} animationDuration={400}>
                            {showCounts && <LabelList dataKey="Returning" position="center" formatter={hideZero} style={SEG_LABEL} />}
                        </Bar>
                    )}
                    {seriesVis.bars && (
                        <Bar yAxisId="left" dataKey="Returned after gap" stackId="a" fill={C.gap} animationDuration={400}>
                            {showCounts && <LabelList dataKey="Returned after gap" position="center" formatter={hideZero} style={SEG_LABEL} />}
                        </Bar>
                    )}
                    {seriesVis.bars && (
                        <Bar yAxisId="left" dataKey="New" stackId="a" fill={C.new} animationDuration={400}>
                            {showCounts && <LabelList dataKey="New" position="center" formatter={hideZero} style={SEG_LABEL} />}
                            {showCounts && <LabelList dataKey="headcount" position="top" offset={14} style={{ fill: theme.palette.text.primary, fontSize: 13, fontWeight: 800 }} />}
                        </Bar>
                    )}
                    {/* Each line = solid for finished terms + a dashed tail into unfinished (provisional) terms. */}
                    {seriesVis.continuation && <Line yAxisId="right" type="monotone" dataKey="continuation__solid" name="Continuation %" stroke={C.line} strokeWidth={2.5} dot={false} connectNulls={false} animationDuration={500} />}
                    {seriesVis.continuation && <Line yAxisId="right" type="monotone" dataKey="continuation__dash" stroke={C.line} strokeWidth={2.5} strokeDasharray="2 3" dot={false} connectNulls={false} legendType="none" animationDuration={500} />}
                    {seriesVis.returning && <Line yAxisId="right" type="monotone" dataKey="carryOver__solid" name="Returning %" stroke={C.line2} strokeWidth={2.5} dot={false} animationDuration={500} />}
                    {seriesVis.returning && <Line yAxisId="right" type="monotone" dataKey="carryOver__dash" stroke={C.line2} strokeWidth={2.5} strokeDasharray="2 3" dot={false} legendType="none" animationDuration={500} />}
                    {seriesVis.avg && <Line yAxisId="avg" type="monotone" dataKey="avgCourses__solid" name="Avg courses/student" stroke={C.line3} strokeWidth={2.5} dot={false} connectNulls animationDuration={500} />}
                    {seriesVis.avg && <Line yAxisId="avg" type="monotone" dataKey="avgCourses__dash" stroke={C.line3} strokeWidth={2.5} strokeDasharray="2 3" dot={false} connectNulls legendType="none" animationDuration={500} />}
                    {seriesVis.retained && <Line yAxisId="right" type="monotone" dataKey="retainedPct__solid" name="Retained %" stroke={C.line4} strokeWidth={2.5} dot={false} animationDuration={500} />}
                    {seriesVis.retained && <Line yAxisId="right" type="monotone" dataKey="retainedPct__dash" stroke={C.line4} strokeWidth={2.5} strokeDasharray="2 3" dot={false} legendType="none" animationDuration={500} />}
                </ComposedChart>
            </ResponsiveContainer>
            <Fade in={!anyComposition} unmountOnExit>
                <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                    <Typography variant="h6" color="text.secondary">Select a graph to show</Typography>
                </Box>
            </Fade>
        </Box>
    );
    // Full metrics table — used as the composition card's "table" view (replaces the old small one).
    const allMetricsTable = (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Year</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Season</TableCell>
                        {["Headcount", "Total enr.", "New", "New %", "Retained %", "Returning", "Carry-over %", "Returned-gap", "Single-skip", "Continuation %", "Courses/student", "Lost", "Net"].map((h) => (
                            <TableCell key={h} sx={{ fontWeight: 700 }} align="right">
                                {COLUMN_FORMULA[h] ? (
                                    <Tooltip arrow placement="top" title={<Box sx={{ fontSize: 13, py: 0.5 }}>{COLUMN_FORMULA[h]}</Box>}>
                                        <span style={{ cursor: "help", borderBottom: "1px dotted currentColor" }}>{h}</span>
                                    </Tooltip>
                                ) : h}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {semsByYear.map((g) => g.rows.map((s, ri) => (
                        <TableRow key={s.label} hover sx={{ backgroundColor: SEASON_BG[Number(s.season)] || "transparent" }}>
                            {ri === 0 && (
                                <TableCell rowSpan={g.rows.length} sx={{ fontWeight: 700, verticalAlign: "top", borderRight: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.background.paper }}>{g.year}</TableCell>
                            )}
                            <TableCell sx={{ fontWeight: 600 }}>
                                {SEASON_NAME[Number(s.season)] || s.label}
                            </TableCell>
                            <TableCell align="right">{s.headcount}</TableCell>
                            <TableCell align="right">{s.enrollmentRows}</TableCell>
                            <TableCell align="right">{s.newCount}</TableCell>
                            <TableCell align="right">{pctLabel(s.newRate)}</TableCell>
                            <TableCell align="right">{pctLabel(s.headcount ? Math.round((s.returningFromLast + s.returnedAfterGap) / s.headcount * 1000) / 10 : null)}</TableCell>
                            <TableCell align="right">{s.returningFromLast}</TableCell>
                            <TableCell align="right">{pctLabel(s.carryOverRate)}</TableCell>
                            <TableCell align="right">{s.returnedAfterGap}</TableCell>
                            <TableCell align="right">{s.singleSkip}</TableCell>
                            <TableCell align="right">
                                {s.continuationRate === null
                                    ? <Tooltip title={s.continuationPending ? "Next semester not finished yet" : "No next semester"}><span>—</span></Tooltip>
                                    : pctLabel(s.continuationRate)}
                            </TableCell>
                            <TableCell align="right">{numLabel(s.enrollmentsPerStudent)}</TableCell>
                            <TableCell align="right" sx={{ color: flowByLabel[s.label]?.lostAbs ? C.line : "inherit" }}>{flowByLabel[s.label]?.lostAbs ?? "—"}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: (flowByLabel[s.label]?.net ?? 0) > 0 ? C.new : (flowByLabel[s.label]?.net ?? 0) < 0 ? C.line : "inherit" }}>{netLabel(flowByLabel[s.label]?.net)}</TableCell>
                        </TableRow>
                    )))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    // --- Cohort survival card (line chart ↔ triangle table) ---
    const cohortChart = (
        <Box height={340}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={cohortLineData} margin={{ top: 10, right: 20, bottom: 34, left: 0 }}>
                    <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
                    <XAxis dataKey="offset" height={48} tick={{ fill: axisColor, fontSize: 12 }}
                        label={{ value: "Semesters after first enrollment  (3 semesters ≈ 1 year)", position: "insideBottom", offset: 0, fill: axisColor, fontSize: 12 }} />
                    <YAxis domain={[0, 100]} tick={{ fill: axisColor, fontSize: 12 }} tickFormatter={(v) => `${v}%`} />
                    <RTooltip contentStyle={tooltipStyle}
                        labelFormatter={(l) => { const n = Number(String(l).replace("+", "")); return n === 0 ? "Joining semester" : `${n} semester${n === 1 ? "" : "s"} after joining`; }}
                        formatter={(v) => (v === null ? "—" : `${v}%`)} />
                    <Legend content={<LegendTips />} />
                    {cohortSeries.map((c, i) => (
                        <Line key={c.label} type="monotone" dataKey={c.label} stroke={cohortColor(i, cohortSeries.length)} strokeWidth={1.5} dot={false} connectNulls isAnimationActive={false} />
                    ))}
                    {/* Bold average curve across all cohorts, drawn on top. */}
                    <Line key="__avg" type="monotone" dataKey="Average" name="Average" stroke={theme.palette.text.primary} strokeWidth={3} dot={{ r: 3 }} connectNulls isAnimationActive={false} />
                </LineChart>
            </ResponsiveContainer>
        </Box>
    );
    const cohortTable = (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Year</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Season</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>New</TableCell>
                        {Array.from({ length: maxOffset }).map((_, k) => (
                            <TableCell key={k} align="center" sx={{ fontWeight: 700 }}>+{k}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {cohortsByYear.map((g) => g.rows.map((r, ri) => {
                        const c = r.c;
                        return (
                            <TableRow key={c.label} sx={{ backgroundColor: SEASON_BG[Number(r.season)] || "transparent" }}>
                                {ri === 0 && (
                                    <TableCell rowSpan={g.rows.length} sx={{ fontWeight: 700, verticalAlign: "top", borderRight: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.background.paper }}>{g.year}</TableCell>
                                )}
                                <TableCell sx={{ fontWeight: 600 }}>{SEASON_NAME[Number(r.season)] || c.label}</TableCell>
                                <TableCell align="right">{c.newStudents}</TableCell>
                                {Array.from({ length: maxOffset }).map((_, k) => {
                                    const cell = c.survival[k];
                                    return (
                                        <TableCell key={k} align="center"
                                            sx={{ backgroundColor: cell ? cohortCellColor(cell.rate) : "transparent", color: cell ? (dark ? "#fff" : "#1a1a1a") : "inherit", fontSize: 12 }}>
                                            {cell ? (cell.rate === null ? "—" : `${cell.rate}%${cell.provisional ? "*" : ""}`) : ""}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        );
                    }))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    // --- Gains & losses cards ---
    const flowTooltip = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null;
        const d = payload[0]?.payload || {};
        const row = (name, val, color) => (
            <Box key={name} sx={{ display: "flex", justifyContent: "space-between", gap: 3 }}>
                <span style={{ color }}>{name}</span><b>{val}</b>
            </Box>
        );
        return (
            <Box sx={{ ...tooltipStyle, p: 1.2, fontSize: 12, minWidth: 190 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>{label}</Typography>
                {d.Retained ? row("Retained", d.Retained, C.ret) : null}
                {row("New", `+${d.New}`, C.new)}
                {d.Reactivated ? row("Reactivated", `+${d.Reactivated}`, C.gap) : null}
                {d.lostAbs != null && row("Lost", `−${d.lostAbs}`, C.line)}
                <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, mt: 0.5, pt: 0.5 }}>
                    {d.net != null && row("Net change", netLabel(d.net), d.net > 0 ? C.new : d.net < 0 ? C.line : theme.palette.text.primary)}
                    {row("Headcount", d.headcount, theme.palette.text.secondary)}
                </Box>
            </Box>
        );
    };
    const divergeChart = (
        <Box sx={{ height: 420 }}>
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={flowData} stackOffset="sign" margin={{ top: 20, right: 20, bottom: 30, left: 0 }}>
                    <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={renderSemTick} interval={0} height={80} />
                    <YAxis tick={{ fill: axisColor, fontSize: 12 }} label={{ value: "students", angle: -90, position: "insideLeft", fill: axisColor }} />
                    <ReferenceLine y={0} stroke={axisColor} />
                    <RTooltip content={flowTooltip} />
                    <Legend content={<LegendTips />} />
                    <Bar dataKey="New" stackId="a" fill={C.new} animationDuration={400} />
                    <Bar dataKey="Reactivated" stackId="a" fill={C.gap} animationDuration={400} />
                    <Bar dataKey="Lost" stackId="a" fill={C.line} animationDuration={400} />
                    <Line type="monotone" dataKey="net" name="Net change" stroke={theme.palette.text.primary} strokeWidth={2.5} dot connectNulls={false} />
                </ComposedChart>
            </ResponsiveContainer>
        </Box>
    );
    const waterfallChart = (
        <Box sx={{ height: 420 }}>
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={flowData} margin={{ top: 20, right: 20, bottom: 30, left: 0 }}>
                    <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={renderSemTick} interval={0} height={80} />
                    <YAxis tick={{ fill: axisColor, fontSize: 12 }} label={{ value: "students", angle: -90, position: "insideLeft", fill: axisColor }} />
                    <RTooltip content={flowTooltip} />
                    <Legend content={<LegendTips />} />
                    {/* Retained + New + Reactivated stack up to the headcount line; Lost is the red cap above it. */}
                    <Bar dataKey="Retained" stackId="w" fill={C.ret} animationDuration={400} />
                    <Bar dataKey="New" stackId="w" fill={C.new} animationDuration={400} />
                    <Bar dataKey="Reactivated" stackId="w" fill={C.gap} animationDuration={400} />
                    <Bar dataKey="lostAbs" name="Lost" stackId="w" fill={C.line} fillOpacity={0.5} animationDuration={400} />
                    <Line type="monotone" dataKey="headcount" name="Headcount" stroke={theme.palette.text.primary} strokeWidth={2} dot />
                </ComposedChart>
            </ResponsiveContainer>
        </Box>
    );

    return (
        <Box m="20px">
            <Box mb="30px">
                <Box display="flex" alignItems="baseline" gap={1.5} flexWrap="wrap" sx={{ mb: "5px" }}>
                    <Typography variant="h2" fontWeight="bold">Retention Analytics</Typography>
                    {currentSemester && (
                        <Typography variant="h4" color="text.secondary" fontWeight={600}>
                            · Current semester: {currentSemester.label}
                            {currentSemester.flags.inProgress && " (in progress)"}
                        </Typography>
                    )}
                </Box>
            </Box>

            <ChartPanel
                title="Enrollment composition & retention"
                controls={
                    <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                        <ToggleButtonGroup size="small"
                            value={Object.keys(seriesVis).filter((k) => seriesVis[k])}
                            onChange={(_, vals) => setSeriesVis({
                                bars: vals.includes("bars"),
                                continuation: vals.includes("continuation"),
                                returning: vals.includes("returning"),
                                avg: vals.includes("avg"),
                                retained: vals.includes("retained"),
                            })}>
                            <ToggleButton value="bars" sx={{ textTransform: "none" }}>Headcount bars</ToggleButton>
                            <ToggleButton value="continuation" sx={{ textTransform: "none", "&.Mui-selected": { color: C.line } }}>Continuation %</ToggleButton>
                            <ToggleButton value="returning" sx={{ textTransform: "none", "&.Mui-selected": { color: C.line2 } }}>Returning %</ToggleButton>
                            <ToggleButton value="avg" sx={{ textTransform: "none", "&.Mui-selected": { color: C.line3 } }}>Avg courses/student</ToggleButton>
                            <ToggleButton value="retained" sx={{ textTransform: "none", "&.Mui-selected": { color: C.line4 } }}>Retained %</ToggleButton>
                        </ToggleButtonGroup>
                        <ToggleButton size="small" value="counts" selected={showCounts} onChange={() => setShowCounts((v) => !v)} sx={{ textTransform: "none" }}>
                            Show numbers
                        </ToggleButton>
                    </Box>
                }
                chart={compositionChart}
                table={allMetricsTable}
                footer={active?.courseBreakdown ? <CourseBreakdown breakdown={active.courseBreakdown} sems={sems} value={breakdownSemLabel} onChange={setSelSem} /> : null}
                info={CARD_INFO.composition}
            />

            <ChartPanel
                title="Semester comparison"
                subtitle="Pick any two semesters (defaults to year-over-year) — compare every metric side by side, charted and tabled with the change."
                chart={<SemesterCompare sems={sems} flowByLabel={flowByLabel} />}
                info={CARD_INFO.compare}
            />

            <ChartPanel
                title="Gains & losses"
                subtitle={flowView === "net"
                    ? "Above zero = gained (New + Reactivated win-backs); below zero = Lost (didn't return from last term). Line = net change."
                    : "Each bar = that term's students by source (Retained + New + Reactivated = the headcount line); translucent red cap = those Lost since last term."}
                controls={
                    <ToggleButtonGroup size="small" exclusive value={flowView} onChange={(_, v) => v && setFlowView(v)}>
                        <ToggleButton value="net" sx={{ textTransform: "none" }}>Net flow</ToggleButton>
                        <ToggleButton value="source" sx={{ textTransform: "none" }}>By source + churn</ToggleButton>
                    </ToggleButtonGroup>
                }
                chart={flowView === "net" ? divergeChart : waterfallChart}
                info={flowView === "net" ? CARD_INFO.flow : CARD_INFO.waterfall}
            />

            <ChartPanel
                title="Cohort survival"
                subtitle="Each cohort = students whose FIRST semester is shown. +N = N SEMESTERS after that (not years — 3 semesters ≈ 1 year); cells/lines = % of the cohort still enrolled then. * = target semester not yet complete."
                chart={cohortChart}
                table={cohortTable}
                info={CARD_INFO.cohort}
            />


        </Box>
    );
};

export default RetentionDashboard;
